// MemberTable.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import "../Home/AppHome.css";
import "../Admin/Admin.css";
import Headers from "./Headers";
import DialogBoxs from "./DialogBoxs";
import { MdEdit, MdDelete, MdClose } from "react-icons/md";
import { useNavigate } from "react-router-dom";

function MemberTable() {
  const [members, setMembers] = useState([]); // all members from backend
  const [editingId, setEditingId] = useState(null); // internal DB id (_id or id)
  const [editForm, setEditForm] = useState({}); // selected member's data
  const [showModal, setShowModal] = useState(false);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [payments, setPayments] = useState({}); // { memberId: [payments...] }
  const [dialog, setDialog] = useState({ show: false, title: "", message: "", type: "", onConfirm: null });
  const [modalMessage, setModalMessage] = useState({ type: "", text: "" });
  const [selectedBranchCode, setSelectedBranchCode] = useState("");

  const [form, setForm] = useState({ amount: "", participation: "", paymentMethod: "", date: "" });
  const navigate = useNavigate();

      useEffect(() => {
        const role = sessionStorage.getItem("userRole");
        if (!role) navigate("/");
      }, [navigate]);

  // --- fetch membership types once ---
  useEffect(() => {
    fetchMembershipTypes();
  }, []);

  const fetchMembershipTypes = async () => {
    try {
      const res = await axios.get("https://gym-invoice-back.onrender.com/api/memberships");
      setMembershipTypes(res.data || []);
    } catch (err) {
      console.error("Error fetching membership types:", err);
    }
  };

  // --- fetch members ---
const fetchMembers = async () => {
  try {
    // 1️⃣ Fetch Members
    const resMembers = await axios.get(
      "https://gym-invoice-back.onrender.com/api/members"
    );

    const allMembers = resMembers.data || [];

    // 2️⃣ Fetch Payments
    const resPayments = await axios.get(
      "https://gym-invoice-back.onrender.com/api/payments"
    );

    const paymentsData = resPayments.data || [];
    const paymentMap = {};

    paymentsData.forEach((p) => {
      if (!paymentMap[p.memberId]) {
        paymentMap[p.memberId] = [];
      }

      paymentMap[p.memberId].push(p);
    });

    // 3️⃣ Filter members
    const filtered = allMembers.filter((m) => {
      const paymentList = paymentMap[m.memberId] || [];

      // Member must be ACTIVE
      const isActive = m.membershipStatus === "ACTIVE";

      // Member must have NO payments
      const hasNoPayments = paymentList.length === 0;

      // Branch filter
      const matchesBranch =
        !selectedBranchCode ||
        m.branchCode === selectedBranchCode;

      return isActive && hasNoPayments && matchesBranch;
    });

    // 4️⃣ Sort by Member ID descending
    const sorted = filtered.sort((a, b) => {
      const idA =
        parseInt((a.memberId || "").replace(/\D/g, "")) || 0;

      const idB =
        parseInt((b.memberId || "").replace(/\D/g, "")) || 0;

      return idB - idA;
    });

    // 5️⃣ Update state
    setMembers(sorted);

  } catch (err) {
    console.error("Error fetching members or payments:", err);
    setMembers([]);
  }
};


  useEffect(() => {
    fetchMembers();
  }, []);

  // --- load payments for all members whenever members list changes ---
  useEffect(() => {
    const load = async () => {
      const temp = {};
      for (let m of members) {
        try {
          if (!m?.memberId) {
            temp[m?.memberId] = [];
            continue;
          }
          const res = await axios.get(`https://gym-invoice-back.onrender.com/api/payments/member/${m.memberId}`);
          temp[m.memberId] = res.data || [];
        } catch (err) {
          temp[m.memberId] = [];
        }
      }
      setPayments(temp);
    };

    if (members.length > 0) load();
    else setPayments({});
  }, [members]);

  // --- helper: fetch payments for a single member and merge into payments state ---
  const fetchPaymentsForMember = async (memberId) => {
    if (!memberId) return;
    try {
      const res = await axios.get(`https://gym-invoice-back.onrender.com/api/payments/member/${memberId}`);
      setPayments((prev) => ({ ...prev, [memberId]: res.data || [] }));
    } catch (err) {
      setPayments((prev) => ({ ...prev, [memberId]: [] }));
    }
  };

  // --- filteredMembers: show member until they have a "real" monthly Done payment ---
  const filteredMembers = members.filter((m) => {
    const payList = payments[m.memberId] || [];

    // consider only "real" monthly payments (not registration or test payments)
    const validPayments = payList.filter((p) =>
      p &&
      p.status === "Done" &&
      p.participation === "Present" &&
      p.paymentMethod &&
      p.billNo &&
      !String(p.billNo).toLowerCase().includes("reg")
    );

    // hide the member only if they are ACTIVE and have a valid monthly Done payment
    if (m.membershipStatus === "ACTIVE" && validPayments.length > 0) return false;

    // otherwise show (new members, active without payment, inactive, etc.)
    return true;
  });

  // --- Open modal directly into Rollup form for the selected member ---
  const handleEditClick = (member) => {
    setEditingId(member.id || member._id || null);

    const normalizedJoined = member.joinedDate && member.joinedDate.substring
      ? member.joinedDate.substring(0, 10)
      : member.joinedDate || "";

    const typeObj = membershipTypes.find((t) => t.type === member.membershipType);
    const fee = typeObj ? typeObj.fee : "";

    const initEdit = { ...member, joinedDate: normalizedJoined, monthlyFee: fee };

    setEditForm(initEdit);
    setForm({ amount: fee || "", participation: "", paymentMethod: "", date: normalizedJoined || "" });

    setModalMessage({ type: "", text: "" });
    setShowModal(true);
  };

  // --- Remove activation/update logic (not needed) ---
  // (We keep no handleUpdate here; activation is removed per your request)

  // --- compute whether rollup form should be enabled ---
  // Keep requirement that membershipStatus is ACTIVE and membershipType exists
  const rollupAllowed = Boolean(editForm && editForm.membershipStatus === "ACTIVE" && editForm.membershipType && editForm.memberId);

  // --- handlerollupubmit: submit first payment ---
  const handlerollupubmit = async (e) => {
    e.preventDefault();

    // basic validation
    if (!form.date && !editForm.joinedDate) {
      setModalMessage({ type: "warning", text: "⚠️ Please select the Bill Cycle Start Date." });
      return;
    }
    if (!form.participation) {
      setModalMessage({ type: "warning", text: "⚠️ Please select participation." });
      return;
    }
    if (form.participation === "Present" && !form.paymentMethod) {
      setModalMessage({ type: "warning", text: "⚠️ Please select Payment Method for Present participation." });
      return;
    }
    if (!editForm.memberId) {
      setModalMessage({ type: "error", text: "❌ Member identifier missing." });
      return;
    }


    try {
      const externalMemberId = editForm.memberId; // used by payments collection

      // Get last bill number and compute next
      const lastPaymentRes = await axios.get("https://gym-invoice-back.onrender.com/api/payments/last-bill");
      let lastBillNo = lastPaymentRes.data?.billNo || "LFP1000";
      let nextBillNo = lastBillNo.startsWith("LFP")
        ? "LFP" + (parseInt(lastBillNo.replace("LFP", ""), 10) + 1)
        : "LFP1000";

      const paymentPayload = {
        memberId: externalMemberId,
        billNo: nextBillNo,
        amount: Number(form.amount || editForm.monthlyFee || 0),
        date: form.date || editForm.joinedDate,
        payDate: new Date().toISOString().substring(0, 10),
        status: "Done",
        paymentMethod: form.paymentMethod || "",
        participation: form.participation,
      };

      // POST payment
      await axios.post("https://gym-invoice-back.onrender.com/api/payments", paymentPayload);

      // Update local payments for this member immediately (so UI updates)
      await fetchPaymentsForMember(externalMemberId);

      // Refresh members (in case backend changes member doc)
      await fetchMembers();

      setDialog({ show: true, type: "success", message: "✅ Rollup saved successfully!" });

      // close modal and reset
      setShowModal(false);
      setEditingId(null);
      setEditForm({});
      setForm({ amount: "", participation: "", paymentMethod: "", date: "" });

      // navigate to invoice view
      navigate(`/success-payment/${nextBillNo}`);
      return;
    } catch (err) {
      console.error("Rollup failed:", err);
      setModalMessage({ type: "error", text: "❌ Failed to save rollup!" });
    }
  };

  const confirmDeleteMember = (member) => {
    setDialog({
      show: true,
      type: "confirm",
      message: "⚠️ Are you sure you want to delete this member?",
      onConfirm: async () => {
        try {
          await axios.delete(`https://gym-invoice-back.onrender.com/api/members/${member.id || member._id}`);
          await fetchMembers();
          setDialog({ show: true, type: "success", message: "✅ Member deleted." });
        } catch (err) {
          console.error(err);
          setDialog({ show: true, type: "error", message: "❌ Delete failed." });
        }
      },
    });
  };

  return (
    <div className="dashboard">
      <Headers />

      <div className="payment-container">
        <h2>📝 Make Member First Payment</h2>

         <div className="branch-filter-container">
                  <label>
                    <strong>Branch Code:</strong>
                  </label>

                  <select
                    value={selectedBranchCode}
                    onChange={(e) => {
                      setSelectedBranchCode(e.target.value);
                    }}
                  >
                    <option value="">All Branches</option>
                    <option value="WB">Werahera Branch</option>
                    <option value="KB">Kesbewa Branch</option>
                  </select>

                  <button
                    type="button"
                    className="pay-button"
                    onClick={fetchMembers}
                  >
                    🔍 Filter
                  </button>


                </div>

        {/* Member Table */}
        <table className="payment-table">
          <thead>
            <tr>
              <th>Member ID</th>
              <th>Full Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMembers.length === 0 ? (
              <tr>
                <td colSpan="3" style={{ textAlign: "center", padding: "15px", fontWeight: "bold", color: "#555" }}>
                  ✅ No members found.
                </td>
              </tr>
            ) : (
              filteredMembers.map((m) => (
                <tr key={m.id || m._id || m.memberId}>
                  <td>{m.memberId}</td>
                  <td>{m.name}</td>
                  <td>
                    <div className="table-action-buttons">
                      <button onClick={() => handleEditClick(m)} className="action-btn edit-btn">
                        <MdEdit size={18} /> First Payment
                      </button>
                      <button onClick={() => confirmDeleteMember(m)} className="action-btn delete-btn">
                        <MdDelete size={18} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Rollup Modal (only rollup UI, no activation) */}
        {showModal && (
          <div className="modal1">
            <div className="modal1-content">
              <button
                className="modal-close-btn"
                onClick={() => {
                  setEditingId(null);
                  setShowModal(false);
                  setModalMessage({ type: "", text: "" });
                }}
              >
                <MdClose />
              </button>

              <div className="payment-card">
                {modalMessage.text && <div className={`modal-message ${modalMessage.type}`}>{modalMessage.text}</div>}

                <h4>First Payment / Rollup — {editForm.name || ""} ({editForm.memberId || ""})</h4>
                <form onSubmit={handlerollupubmit}>
                  <div className="form-row">
                    <label>Membership Type</label>
                    <input type="text" value={editForm.membershipType || ""} readOnly />
                  </div>

                  <div className="form-row">
                    <label>Amount (Rs.)</label>
                    <input type="number" value={form.amount || editForm.monthlyFee || ""} readOnly />
                  </div>

                  <div className="form-row">
                    <label>Bill Cycle Start Date</label>
                    <input
                      type="date"
                      name="date"
                      value={form.date || editForm.joinedDate || ""}
                      onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
                    />
                  </div>

                  <div className="form-row">
                    <label>Participation</label>
                    <select
                      value={form.participation}
                      onChange={(e) => setForm((prev) => ({ ...prev, participation: e.target.value }))}
                      required
                    >
                      <option value="">Select Participation</option>
                      <option value="Present">Present</option>

                    </select>
                  </div>

                  {form.participation === "Present" && (
                    <div className="form-row">
                      <label>Payment Method</label>
                      <select
                        value={form.paymentMethod}
                        onChange={(e) => setForm((prev) => ({ ...prev, paymentMethod: e.target.value }))}
                        required
                      >
                        <option value="">Select Method</option>
                        <option value="Cash">Cash</option>
                        <option value="Online">Online</option>
                      </select>
                    </div>
                  )}

                  <div className="payment-buttons">
                    <button className="pay-button" type="submit" disabled={!rollupAllowed}>
                      ✅ Submit
                    </button>
                    <button
                      className="pay-button"
                      type="button"
                      onClick={() => {
                        setEditingId(null);
                        setShowModal(false);
                        setModalMessage({ type: "", text: "" });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        <DialogBoxs dialog={dialog} setDialog={setDialog} />
      </div>
    </div>
  );
}

export default MemberTable;
